import { motion } from "framer-motion";
import { MapPin, Phone, Train, Truck, ShoppingBag, Heart, Bike } from "lucide-react";
import { useLang } from "@/i18n";

const HOURS = ["13:00 – 00:00", "11:00 – 01:00", "11:00 – 01:00", "11:00 – 01:00", "11:00 – 01:00", "11:00 – 04:00", "11:00 – 04:00"];
const FEATURE_ICONS = [Truck, ShoppingBag, Heart];

const InfoSection = () => {
  const { t } = useLang();
  const todayIdx = (new Date().getDay() + 6) % 7;

  return (
    <section id="infos" className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32" data-testid="info-section">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8 }}
        className="mb-12"
      >
        <p className="mb-3 font-mono2 text-xs uppercase tracking-[0.3em] text-[#A9761F]">{t.info.overline}</p>
        <h2 className="font-display text-3xl font-semibold tracking-tight text-[#C25E18] sm:text-4xl lg:text-5xl" data-testid="info-title">
          {t.info.title}
        </h2>
      </motion.div>

      <div className="grid gap-6 lg:grid-cols-2">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
          className="rounded-2xl border border-[#EBDDC6] bg-[#FFFBF3] p-6 sm:p-8"
        >
          <div className="space-y-6">
            <div className="flex gap-4">
              <MapPin className="mt-1 shrink-0 text-[#E07A2B]" size={20} />
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-[#A08B74]">{t.info.addressLabel}</p>
                <p className="mt-1 text-[#3B2A1E]" data-testid="info-address">{t.info.address1}</p>
                <p className="text-[#7A6753]">{t.info.address2}</p>
                <a href="https://www.google.com/maps/dir/?api=1&destination=50.84014950,4.33416480" target="_blank" rel="noopener noreferrer"
                  className="mt-2 inline-block text-sm text-[#A9761F] underline-offset-4 transition-colors hover:text-[#E07A2B] hover:underline"
                  data-testid="info-map-link">
                  {t.info.map}
                </a>
              </div>
            </div>
            <div className="flex gap-4">
              <Train className="mt-1 shrink-0 text-[#E07A2B]" size={20} />
              <p className="mt-0.5 text-[#7A6753]" data-testid="info-metro">{t.info.metro}</p>
            </div>
            <div className="flex gap-4">
              <Phone className="mt-1 shrink-0 text-[#E07A2B]" size={20} />
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-[#A08B74]">{t.info.phoneLabel}</p>
                <a href="tel:+32470313554" className="mt-1 block text-lg font-medium text-[#3B2A1E] transition-colors hover:text-[#E07A2B]" data-testid="info-phone">
                  +32 470 31 35 54
                </a>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pt-2" data-testid="info-features">
              {t.info.features.map((f, i) => {
                const Icon = FEATURE_ICONS[i];
                return (
                  <span key={f} className="inline-flex items-center gap-1.5 rounded-full border border-[#EBDDC6] bg-[#F5E9D7] px-3.5 py-1.5 text-xs text-[#7A6753]">
                    <Icon size={13} className="text-[#A9761F]" /> {f}
                  </span>
                );
              })}
            </div>
            <a href="https://noldgo.com/restaurants/c6369716-98f1-4462-a9e4-2b9c24324a10" target="_blank" rel="noopener noreferrer" data-testid="info-partner-noldgo"
              className="flex items-center gap-3 rounded-xl border border-[#A9761F]/30 bg-[#F5E9D7] px-4 py-3 transition-all duration-300 hover:border-[#E07A2B]/60">
              <Bike size={18} className="shrink-0 text-[#E07A2B]" />
              <div>
                <p className="text-sm font-semibold text-[#3B2A1E]">Noldgo.com</p>
                <p className="text-xs text-[#A08B74]">{t.info.partnerText}</p>
              </div>
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="rounded-2xl border border-[#EBDDC6] bg-[#FFFBF3] p-6 sm:p-8"
        >
          <p className="mb-4 text-xs font-medium uppercase tracking-wider text-[#A08B74]">{t.info.hours}</p>
          <div className="divide-y divide-[#EBDDC6]/60" data-testid="info-hours">
            {t.info.days.map((day, i) => (
              <div key={day} className={`flex items-center justify-between py-2.5 text-sm ${i === todayIdx ? "text-[#E07A2B]" : "text-[#7A6753]"}`}
                data-testid={`info-hours-${i}`}>
                <span className="flex items-center gap-2">
                  {day}
                  {i === todayIdx && <span className="rounded-full bg-[#E07A2B]/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide">{t.info.today}</span>}
                </span>
                <span className="font-mono2">{HOURS[i]}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default InfoSection;
